import React, { useState, useRef, useEffect } from 'react';
import theme from '../config/theme';
import { FaChevronDown } from 'react-icons/fa';

// Dropdown Option interface
export interface DropdownOption {
  value: string | number;
  label: string;
  disabled?: boolean;
}

// Base Dropdown Props interface
interface BaseDropdownProps {
  options: DropdownOption[];
  value?: string | number;
  onChange?: (value: string | number) => void;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  error?: boolean;
  errorMessage?: string;
  label?: string;
  required?: boolean;
  clearable?: boolean;
  vertical?: boolean;
}

// Simple Dropdown Props (ไม่มี search)
interface SimpleDropdownProps extends BaseDropdownProps {
  searchable?: false;
}

// Searchable Dropdown Props (มี search)
interface SearchableDropdownProps extends BaseDropdownProps {
  searchable: true;
  searchPlaceholder?: string;
}

// Union type for all dropdown props
type DropdownProps = SimpleDropdownProps | SearchableDropdownProps;

// Simple Dropdown Component (ไม่มี search)
const SimpleDropdown: React.FC<SimpleDropdownProps> = ({
  options = [],
  value,
  onChange,
  placeholder = 'เลือกตัวเลือก',
  disabled = false,
  className = '',
  error = false,
  errorMessage,
  label,
  required = false,
  clearable = false,
  vertical = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedOption, setSelectedOption] = useState<DropdownOption | null>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Find selected option based on value
  useEffect(() => {
    const option = options.find(opt => opt.value === value);
    setSelectedOption(option || null);
  }, [value, options]);

  // Handle option selection
  const handleOptionSelect = (option: DropdownOption) => {
    if (option.disabled) return;
    
    setSelectedOption(option);
    onChange?.(option.value);
    setIsOpen(false);
  };

  // Handle clear selection
  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    setSelectedOption(null);
    onChange?.('');
  };

  // Handle toggle dropdown
  const handleToggle = () => {
    if (disabled) return;
    setIsOpen(!isOpen);
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const baseClasses = 'w-full px-4 py-3 rounded-lg border focus:outline-none focus:ring-2 focus:border-transparent transition-all';
  const disabledClasses = disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer';
  
  const dropdownClasses = `${baseClasses} ${disabledClasses} ${className}`;

  return (
    <div className={`flex ${vertical ? 'flex-col' : 'flex-row'} gap-2 items-center ${className}`}>
      {label && (
        <div className="block text-sm mb-2" 
        style={{minWidth: !vertical ? '100px' : 'auto', color: theme.color.gray400 }}
        >
          {label}
          {required && <span className="ml-1" style={{ color: theme.color.error }}>*</span>}
        </div>
      )}
      
      <div ref={dropdownRef} className="relative w-full">
        {/* Dropdown Trigger */}
        <div
          className={dropdownClasses}
          style={{
            backgroundColor: theme.color.light100,
            borderColor: error ? theme.color.error : theme.color.light500,
            color: theme.color.gray500
          }}
          onClick={handleToggle}
        >
          <div className="flex items-center justify-between">
            <span style={{ color: selectedOption ? theme.color.gray500 : theme.color.gray100 }}>
              {selectedOption ? selectedOption.label : placeholder}
            </span>
            <div className="flex items-center space-x-2">
              {clearable && selectedOption && !disabled && (
                <button
                  type="button"
                  onClick={handleClear}
                  style={{ color: theme.color.gray200 }}
                  className="hover:opacity-70 transition-colors"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              )}
              {React.createElement(FaChevronDown as any, {
                className: `w-4 h-4 transition-transform ${isOpen ? 'rotate-180' : ''}`,
                style: { color: theme.color.gray500 }
              })}
            </div>
          </div>
        </div>

        {/* Dropdown Menu */}
        {isOpen && (
          <div 
            className="absolute z-50 w-full mt-1 rounded-lg shadow-lg max-h-60 overflow-hidden border"
            style={{
              backgroundColor: theme.color.light100,
              borderColor: theme.color.light300
            }}
          >
            {/* Options List */}
            <div className="max-h-48 overflow-y-auto">
              {options.length > 0 ? (
                options.map((option, index) => (
                  <div
                    key={option.value}
                    className="px-4 py-3 cursor-pointer transition-colors"
                    style={{
                      color: option.disabled ? theme.color.gray100 : theme.color.gray500,
                      backgroundColor: selectedOption?.value === option.value ? theme.color.light200 : 'transparent',
                      cursor: option.disabled ? 'not-allowed' : 'pointer'
                    }}
                    onMouseEnter={(e) => {
                      if (!option.disabled && selectedOption?.value !== option.value) {
                        e.currentTarget.style.backgroundColor = theme.color.light200;
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (selectedOption?.value !== option.value) {
                        e.currentTarget.style.backgroundColor = 'transparent';
                      }
                    }}
                    onClick={() => handleOptionSelect(option)}
                  >
                    {option.label}
                  </div>
                ))
              ) : (
                <div className="px-4 py-6 text-center" style={{ color: theme.color.gray300 }}>
                  <div className="flex flex-col items-center">
                    <svg className="w-8 h-8 mb-2" style={{ color: theme.color.gray200 }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <p className="text-sm">ไม่มีตัวเลือก</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Error Message */}
      {error && errorMessage && (
        <p className="mt-1 text-sm" style={{ color: theme.color.error }}>{errorMessage}</p>
      )}
    </div>
  );
};

// Searchable Dropdown Component (มี search)
const SearchableDropdown: React.FC<SearchableDropdownProps> = ({
  options = [],
  value,
  onChange,
  placeholder = 'เลือกตัวเลือก',
  disabled = false,
  className = '',
  error = false,
  errorMessage,
  label,
  required = false,
  clearable = false,
  searchPlaceholder = 'ค้นหา...'
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedOption, setSelectedOption] = useState<DropdownOption | null>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);

  // Find selected option based on value
  useEffect(() => {
    const option = options.find(opt => opt.value === value);
    setSelectedOption(option || null);
  }, [value, options]);

  // Filter options based on search term with better matching
  const filteredOptions = options.filter(option => {
    const searchLower = searchTerm.toLowerCase().trim();
    const labelLower = option.label.toLowerCase();
    
    // Exact match first
    if (labelLower === searchLower) return true;
    
    // Starts with search term
    if (labelLower.startsWith(searchLower)) return true;
    
    // Contains search term
    if (labelLower.includes(searchLower)) return true;
    
    // Word boundary matching
    const words = labelLower.split(/\s+/);
    return words.some(word => word.startsWith(searchLower));
  });

  // Handle option selection
  const handleOptionSelect = (option: DropdownOption) => {
    if (option.disabled) return;
    
    setSelectedOption(option);
    onChange?.(option.value);
    setIsOpen(false);
    setSearchTerm('');
  };

  // Handle clear selection
  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    setSelectedOption(null);
    onChange?.('');
  };

  // Handle toggle dropdown
  const handleToggle = () => {
    if (disabled) return;
    setIsOpen(!isOpen);
    if (!isOpen && searchRef.current) {
      searchRef.current.focus();
    }
  };

  // Handle search input change
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
  };

  // Handle keyboard navigation
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!isOpen) return;

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      // Focus next option (simplified - could be enhanced)
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      // Focus previous option (simplified - could be enhanced)
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (filteredOptions.length > 0) {
        handleOptionSelect(filteredOptions[0]);
      }
    } else if (e.key === 'Escape') {
      e.preventDefault();
      setIsOpen(false);
      setSearchTerm('');
    }
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setSearchTerm('');
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const baseClasses = 'w-full px-4 py-3 rounded-lg border focus:outline-none focus:ring-2 focus:border-transparent transition-all';
  const disabledClasses = disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer';
  
  const dropdownClasses = `${baseClasses} ${disabledClasses} ${className}`;

  return (
    <div className="relative">
      {label && (
        <label className="block text-sm mb-2" style={{ color: theme.color.gray400 }}>
          {label}
          {required && <span className="ml-1" style={{ color: theme.color.error }}>*</span>}
        </label>
      )}
      
      <div ref={dropdownRef} className="relative">
        {/* Dropdown Trigger */}
        <div
          className={dropdownClasses}
          style={{
            backgroundColor: theme.color.light100,
            borderColor: error ? theme.color.error : theme.color.light500,
            color: theme.color.gray500
          }}
          onClick={handleToggle}
        >
          <div className="flex items-center justify-between">
            <span style={{ color: selectedOption ? theme.color.gray500 : theme.color.gray100 }}>
              {selectedOption ? selectedOption.label : placeholder}
            </span>
            <div className="flex items-center space-x-2">
              {clearable && selectedOption && !disabled && (
                <button
                  type="button"
                  onClick={handleClear}
                  style={{ color: theme.color.gray200 }}
                  className="hover:opacity-70 transition-colors"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              )}
              {React.createElement(FaChevronDown as any, {
                className: `w-4 h-4 transition-transform ${isOpen ? 'rotate-180' : ''}`,
                style: { color: theme.color.gray200 }
              })}
            </div>
          </div>
        </div>

        {/* Dropdown Menu */}
        {isOpen && (
          <div 
            className="absolute z-50 w-full mt-1 rounded-lg shadow-lg max-h-60 overflow-hidden border"
            style={{
              backgroundColor: theme.color.light100,
              borderColor: theme.color.light300
            }}
          >
            {/* Search Input */}
            <div className="p-3 border-b" style={{ borderColor: theme.color.light300 }}>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <svg className="h-4 w-4" style={{ color: theme.color.gray200 }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                </div>
                <input
                  ref={searchRef}
                  type="text"
                  value={searchTerm}
                  onChange={handleSearchChange}
                  onKeyDown={handleKeyDown}
                  placeholder={searchPlaceholder}
                  className="w-full pl-10 pr-4 py-2 rounded-lg border focus:outline-none focus:ring-2 focus:border-transparent"
                  style={{
                    backgroundColor: theme.color.light100,
                    borderColor: theme.color.light500,
                    color: theme.color.gray500
                  }}
                />
                {searchTerm && (
                  <button
                    type="button"
                    onClick={() => setSearchTerm('')}
                    className="absolute inset-y-0 right-0 pr-3 flex items-center hover:opacity-70 transition-colors"
                    style={{ color: theme.color.gray200 }}
                  >
                    <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                )}
              </div>
            </div>

            {/* Options List */}
            <div className="max-h-48 overflow-y-auto">
              {filteredOptions.length > 0 ? (
                filteredOptions.map((option, index) => {
                  // Highlight search term in option label
                  const highlightText = (text: string, searchTerm: string) => {
                    if (!searchTerm.trim()) return text;
                    
                    const regex = new RegExp(`(${searchTerm.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi');
                    const parts = text.split(regex);
                    
                    return parts.map((part, i) => 
                      regex.test(part) ? (
                        <mark key={i} className="px-1 rounded" style={{ backgroundColor: theme.brandColor.yellow + '40', color: theme.brandColor.yellow }}>
                          {part}
                        </mark>
                      ) : part
                    );
                  };

                  return (
                    <div
                      key={option.value}
                      className="px-4 py-3 cursor-pointer transition-colors"
                      style={{
                        color: option.disabled ? theme.color.gray100 : theme.color.gray500,
                        backgroundColor: selectedOption?.value === option.value ? theme.color.light200 : 'transparent',
                        cursor: option.disabled ? 'not-allowed' : 'pointer'
                      }}
                      onMouseEnter={(e) => {
                        if (!option.disabled && selectedOption?.value !== option.value) {
                          e.currentTarget.style.backgroundColor = theme.color.light200;
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (selectedOption?.value !== option.value) {
                          e.currentTarget.style.backgroundColor = 'transparent';
                        }
                      }}
                      onClick={() => handleOptionSelect(option)}
                    >
                      {searchTerm ? highlightText(option.label, searchTerm) : option.label}
                    </div>
                  );
                })
              ) : (
                <div className="px-4 py-6 text-center" style={{ color: theme.color.gray300 }}>
                  <div className="flex flex-col items-center">
                    <svg className="w-8 h-8 mb-2" style={{ color: theme.color.gray200 }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                    </svg>
                    <p className="text-sm">
                      {searchTerm ? 'ไม่พบตัวเลือกที่ค้นหา' : 'ไม่มีตัวเลือก'}
                    </p>
                    {searchTerm && (
                      <p className="text-xs mt-1" style={{ color: theme.color.gray200 }}>
                        ลองค้นหาด้วยคำอื่น
                      </p>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Error Message */}
      {error && errorMessage && (
        <p className="mt-1 text-sm" style={{ color: theme.color.error }}>{errorMessage}</p>
      )}
    </div>
  );
};

// Main Dropdown Component (auto-detect based on searchable prop)
const Dropdown: React.FC<DropdownProps> = (props) => {
  if (props.searchable === true) {
    return <SearchableDropdown {...props} />;
  }
  return <SimpleDropdown {...props} />;
};

// Export all components
export default Dropdown;
export { SimpleDropdown, SearchableDropdown };